import moment from 'moment';

import config from '../../../config';
import { PASS_ANNUAL_PASS_VOUCHER } from '../../../util/annualPass';

/**
 * The annual pass voucher the breakdown reads its coverage from. Older
 * transactions carry it as `metadata.voucher`, newer ones as
 * `metadata.annualPassVoucher`.
 *
 * @param {Object} transaction transaction (or estimated transaction)
 * @returns {Object|undefined} the voucher payload, if any
 */
const annualPassVoucherOf = (transaction) => {
  const metadata = transaction.attributes.metadata || {};
  return metadata.annualPassVoucher || metadata.voucher;
};

/**
 * Whether the customer's annual pass covers the whole stay, i.e. every night of
 * it is free and neither the annual pass voucher nor the nightly commission is
 * charged.
 *
 * This is the predicate the night rows, the annual pass voucher row and the
 * customer commission row all used to carry their own copy of. Plus is read
 * from `included.plus` only — a `plus` flag directly on the voucher payload is
 * ignored, matching how the server reads vouchers (it accepts a top-level
 * `plus` only on the profile pass metadata, never on the voucher). See the
 * addendum in `docs/superpowers/plans/2026-07-30-breakdown-mutation-cleanup.md`.
 *
 * @param {Object} transaction transaction (or estimated transaction) with booking and metadata
 * @param {Object|null} pass the current user's annual pass, if any
 * @returns {boolean}
 */
export const passCoversWholeStay = (transaction, pass) => {
  const annualPassVoucher = annualPassVoucherOf(transaction);

  if (!annualPassVoucher || annualPassVoucher.type !== PASS_ANNUAL_PASS_VOUCHER) return false;

  const bookingEnd = transaction.booking?.attributes?.end;
  const coversAllNights =
    !!pass && !!bookingEnd && moment.utc(bookingEnd).isBefore(moment.utc(pass.validUntil));

  if (!coversAllNights) return false;

  const isPlus = annualPassVoucher?.included?.plus;
  const isOldOrPlusVoucher =
    !annualPassVoucher?.included ||
    annualPassVoucher?.included?.plus === undefined ||
    annualPassVoucher?.included?.plus === true;

  if (isOldOrPlusVoucher || isPlus) return true;

  // A light pass only covers listings below the plus price threshold.
  const originalListingPrice = transaction.attributes.metadata?.originalListingPrice;
  return (
    !!originalListingPrice && originalListingPrice.amount < config.annualPass.lightPriceThreshold
  );
};

/**
 * Whether the pass itself includes an extra line item (bed linen, electricity,
 * …), which makes it free regardless of the stay's dates.
 *
 * Unlike `passCoversWholeStay` this looks at the pass only — not at the
 * transaction's voucher metadata and not at the booking end. It is the
 * predicate the extra cost rows have always used.
 *
 * @param {Object} lineItem the line item to check
 * @param {Object|null} pass the current user's annual pass, if any
 * @param {boolean} hasValidPass whether that pass is valid right now
 * @returns {boolean}
 */
export const passIncludesExtraLineItem = (lineItem, pass, hasValidPass) =>
  !!hasValidPass && !!pass?.included?.extraLineItems?.includes(lineItem.code);
